import { AnyObject } from "core/types";
import { fromJS, is } from "immutable";
import React from "react";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, scan, share } from "rxjs/operators";
import { Locale, withTranslations } from "translations";
import Translations from "translations/types";
import { ObservableValue } from "./Context";
import { WithChildren } from "./Form";
import {
  INITIAL_CHANGE_EVENT_TYPE,
  RESET_VALIDATION_EVENT_TYPE,
  SUBMIT_EVENT_TYPE,
  VALIDATION_FAILED_EVENT_TYPE,
  VALUE_CHANGE_EVENT_TYPE,
  createInitialChangeEvent,
  createResetValidationEventType,
  createSubmitChangeEvent,
  createValidationFailedEvent,
  createValueChangeEvent,
} from "./FormEvents";
import { setValue } from "./StateValueHelpers";
import { ValidationFunction, Whitelist } from "./types";
import { Validation } from "./ValidationHelpers";

// types
type WithWhitelist = {
  whitelist: Whitelist;
};

type WithTranslations = {
  locale: Locale;
  translations: Translations;
};

type FormContainerWithTranslationsProps = WithChildren &
  WithWhitelist & {
    convertIn: (value: any, props?: FormContainerWithTranslationsProps) => any;
    convertOut: (value: any, props?: FormContainerWithTranslationsProps) => any;
    formInputValue?: AnyObject | null;
    onSubmit: (value: any, state: any, e: any) => void;
    resetAfterSubmit?: boolean;
    validate: ValidationFunction;
    validateConvertedOut?: ValidationFunction;
  };

type FormContainerProps = FormContainerWithTranslationsProps & WithTranslations;

export type FormContainerState = {
  dirty: boolean;
};

function getNewValidation(acc: ObservableValue, next: ObservableValue) {
  switch (next.type) {
    case VALUE_CHANGE_EVENT_TYPE:
    case INITIAL_CHANGE_EVENT_TYPE:
    case VALIDATION_FAILED_EVENT_TYPE:
      return next.validation || acc.validation;
    default:
      return acc.validation;
  }
}

function getInitialValue(props: FormContainerProps) {
  return props.convertIn(
    !props.formInputValue ? {} : props.formInputValue,
    props,
  );
}

class FormContainer extends React.Component<
  FormContainerProps,
  FormContainerState
> {
  subject: Subject<ObservableValue>;
  observable: Observable<ObservableValue>;
  valueChangeObs: Observable<ObservableValue>;

  constructor(props: FormContainerProps) {
    super(props);

    this.subject = new Subject<ObservableValue>();

    this.observable = this.subject.pipe(
      scan(
        (acc, next) => {
          switch (next.type) {
            case VALUE_CHANGE_EVENT_TYPE: {
              return {
                oldValue: acc.value,
                statePath: next.statePath,
                type: VALUE_CHANGE_EVENT_TYPE,
                validation: getNewValidation(acc, next),
                value: setValue(acc.value, next.statePath ?? "", next.newValue),
              };
            }
            case SUBMIT_EVENT_TYPE:
              return {
                clickEvent: next.clickEvent,
                type: SUBMIT_EVENT_TYPE,
                validation: acc.validation,
                value: acc.value,
              };
            case INITIAL_CHANGE_EVENT_TYPE:
              return {
                oldValue: acc.value,
                type: INITIAL_CHANGE_EVENT_TYPE,
                validation: getNewValidation(acc, next),
                value: next.newValue,
              };
            case VALIDATION_FAILED_EVENT_TYPE:
              return {
                type: VALIDATION_FAILED_EVENT_TYPE,
                validation: next.validation,
                value: acc.value,
              };
            case RESET_VALIDATION_EVENT_TYPE: {
              // if validation is already passing do nothing
              if (acc.validation === true) {
                return {
                  type: RESET_VALIDATION_EVENT_TYPE,
                  validation: acc.validation,
                  value: acc.value,
                };
              }

              // if no paths passed in reset complete error state
              if (
                !next.paths?.length ||
                !acc.validation ||
                typeof acc.validation !== "object" ||
                typeof acc.validation.infos !== "object"
              ) {
                return {
                  type: RESET_VALIDATION_EVENT_TYPE,
                  validation: new Validation(),
                  value: acc.value,
                };
              }

              // if paths passed in reset error state for those state paths
              const newValidationInfos = Object.entries(
                acc.validation.infos,
              ).reduce((curr, [statePath, validation]) => {
                if (!next.paths?.some((path) => statePath.includes(path))) {
                  return { ...curr, [statePath]: validation };
                }
                return curr;
              }, {});

              return {
                type: RESET_VALIDATION_EVENT_TYPE,
                validation: new Validation(newValidationInfos),
                value: acc.value,
              };
            }
            default:
              return acc;
          }
        },
        {
          type: INITIAL_CHANGE_EVENT_TYPE,
          validation: true,
          value: getInitialValue(props),
        } as ObservableValue,
      ),
      share(),
    );

    this.observable
      .pipe(filter((e) => e.type === SUBMIT_EVENT_TYPE))
      .subscribe({
        next: (e) => this.submit(e.value, e.clickEvent!),
      });

    this.valueChangeObs = this.observable.pipe(
      filter((e) => {
        return (
          e.type === VALUE_CHANGE_EVENT_TYPE ||
          e.type === INITIAL_CHANGE_EVENT_TYPE ||
          e.type === VALIDATION_FAILED_EVENT_TYPE ||
          e.type === RESET_VALIDATION_EVENT_TYPE
        );
      }),
      share({
        connector: () =>
          new BehaviorSubject<ObservableValue>({
            type: INITIAL_CHANGE_EVENT_TYPE,
            validation: true,
            value: getInitialValue(props),
            whitelist: props.whitelist,
          }),
        resetOnComplete: false,
        resetOnError: false,
        resetOnRefCountZero: false,
      }),
    );

    this.state = {
      dirty: false,
    };
  }

  componentDidUpdate(prevProps: FormContainerProps) {
    const newInitial = getInitialValue(this.props);
    const oldInitial = getInitialValue(prevProps);
    if (
      oldInitial != null &&
      "toJS" in oldInitial &&
      newInitial != null &&
      "toJS" in newInitial
    ) {
      if (newInitial !== oldInitial)
        this.subject.next(createInitialChangeEvent(newInitial));
    } else if (oldInitial == null && newInitial != null) {
      this.subject.next(createInitialChangeEvent(newInitial));
    } else if (oldInitial != null && newInitial != null) {
      if (!is(fromJS(newInitial), fromJS(oldInitial)))
        this.subject.next(createInitialChangeEvent(newInitial));
    }
  }

  componentWillUnmount() {
    this.subject.unsubscribe();
  }

  submit(value: any, clickEvent: React.MouseEvent<HTMLButtonElement>) {
    if (this.props.onSubmit == null) {
      console.warn(
        "Form hasn't received any onSubmit function as props, you may have forgotten it",
      );
      return;
    }

    const validation = this.props.validate(value, this.props);

    // Early return if validation fails
    if (validation !== true && validation != null) {
      const newValidation = new Validation(validation);

      if (process.env.NODE_ENV === "development") {
        console.groupCollapsed(
          "%c Form validation failed",
          "background: red; color: white",
          "",
        );
        console.log(JSON.stringify(newValidation));
        console.groupEnd();
      }

      this.subject.next(createValidationFailedEvent(newValidation));
      return;
    }

    const convertedOutput = this.props.convertOut(value, this.props);

    const outputValidation = this.props.validateConvertedOut?.(
      convertedOutput,
      this.props,
    );

    // Early return if validation fails
    if (outputValidation !== true && outputValidation != null) {
      const newValidation = new Validation(outputValidation);

      if (process.env.NODE_ENV === "development") {
        console.groupCollapsed(
          "%c Form outputValidation failed",
          "background: red; color: white",
          "",
        );
        console.log(JSON.stringify(newValidation));
        console.groupEnd();
      }

      this.subject.next(createValidationFailedEvent(newValidation));
      return;
    }

    this.props.onSubmit(convertedOutput, this.state, clickEvent);

    if (this.state.dirty) {
      this.setState({ dirty: false });
    }

    if (this.props.resetAfterSubmit) {
      this.subject.next(
        createValueChangeEvent(getInitialValue(this.props), "", true),
      );
    }
  }

  render() {
    return this.props.children({
      dirty: this.state.dirty,
      onChange: (value, statePath, validation) => {
        if (!this.state.dirty) {
          this.setState({ dirty: true });
        }
        this.subject.next(
          createValueChangeEvent(value, statePath || "", validation),
        );
      },
      resetValidation: (paths?: ObservableValue["paths"]) => {
        this.subject.next(createResetValidationEventType(paths));
      },
      submit: (clickEvent) => {
        return this.subject.next(createSubmitChangeEvent(clickEvent));
      },
      valueChangeObs: this.valueChangeObs,
    });
  }
}

export const FormContainerWithTranslations = withTranslations(FormContainer);
